/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ITextField } from '@fluentui/react/lib/TextField';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { TagInputField } from 'src/core/components/TagInputField';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { noop } from 'src/core/utils/base';
import { PhraseListTagsContainer, PhraseListTagsContainerProps } from 'src/luis/modules/features/components/PhraseListTagsContainer';

export type PhraseListValuesContainerProps = Pick<PhraseListTagsContainerProps, 'items' | 'onRemoveItems'> & {
    onAddValues: (values: string[]) => void;
    onRemoveLastItem: () => void;

    /**
     * Whether the user can add, delete or perform any action in the container.
     */
    areValuesEditable?: boolean;
};

const messages = defineMessages({
    valuesContainerTitle: {
        id: 'features.phraseListModal.valuesContainerTitle',
        defaultMessage: 'Values'
    }
});

/**
 * Represents a container for the phrase list values.
 */
export const PhraseListValuesContainer = (props: PhraseListValuesContainerProps) => {
    const { items, onRemoveItems, onRemoveLastItem, onAddValues, areValuesEditable = true } = props;
    const { localize } = useLocalization();
    const textField = React.useRef<ITextField>();

    return (
        <PhraseListTagsContainer
            isRequired
            items={items}
            itemsMode="remove"
            onRemoveItems={onRemoveItems}
            legend={localize(messages.valuesContainerTitle)}
            onContainerClick={() => areValuesEditable && textField.current.focus()}
            areValuesEditable={areValuesEditable}>
            <TagInputField
                isRequired={items.length === 0}
                ref={textField}
                submitChars={['Enter', ',']}
                onSubmit={onAddValues}
                onEmptyFieldBackspace={areValuesEditable ? onRemoveLastItem : noop}
                isAddingValuesDisabled={!areValuesEditable}
            />
        </PhraseListTagsContainer>
    );
};
